import { useState } from 'react';
import { Box, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { TriggerInterruption } from '@shared/interfaces/assistantScript';
import {
  TriggerSectionTitle,
  TriggerSectionSubtitle,
} from '../AssistantScriptDialog.styled';
import {
  InterruptSectionContainer,
  InterruptMenuItemRow,
  InterruptMenuItemTitle,
  InterruptMenuItemDescription,
  InterruptMenuPauseIcon,
  InterruptMenuStopIcon,
  InterruptMenuLockIcon,
  BridgeGapsBox,
  BridgeGapsTitle,
  BridgeGapsDescription,
  InterruptDropdown,
  InterruptDropdownHeader,
  InterruptDropdownItem,
  InterruptExpandIcon,
  InterruptBadge,
  InterruptRadioOuter,
  InterruptRadioInner,
} from './styled';

interface InterruptSectionProps {
  interruptMode: TriggerInterruption;
  onInterruptModeChange: (mode: TriggerInterruption) => void;
  bridgeRegionGaps: boolean;
  onBridgeRegionGapsChange: (bridge: boolean) => void;
}

type Option = {
  value: TriggerInterruption;
  icon: JSX.Element;
  label: string;
  desc: string;
  badge?: string;
};

const InterruptSection = ({
  interruptMode,
  onInterruptModeChange,
  bridgeRegionGaps,
  onBridgeRegionGapsChange,
}: InterruptSectionProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const options: Option[] = [
    {
      value: 'finish_step',
      icon: <InterruptMenuPauseIcon />,
      label: t('assistant.scriptDialog.interruptFinishStep'),
      desc: t('assistant.scriptDialog.interruptFinishStepDesc'),
      badge: t('assistant.scriptDialog.policies.once_session.badge'),
    },
    {
      value: 'hard_cut',
      icon: <InterruptMenuStopIcon />,
      label: t('assistant.scriptDialog.interruptHardCut'),
      desc: t('assistant.scriptDialog.interruptHardCutDesc'),
    },
    {
      value: 'non_interruptible',
      icon: <InterruptMenuLockIcon />,
      label: t('assistant.scriptDialog.interruptNonInterruptible'),
      desc: t('assistant.scriptDialog.interruptNonInterruptibleDesc'),
    },
  ];

  const selected = options.find((o) => o.value === interruptMode) ?? options[0];

  return (
    <InterruptSectionContainer>
      <TriggerSectionTitle>
        {t('assistant.scriptDialog.whenInterrupted')}
      </TriggerSectionTitle>
      <TriggerSectionSubtitle>
        {t('assistant.scriptDialog.whenInterruptedSubtitle')}
      </TriggerSectionSubtitle>

      <InterruptDropdown $open={open}>
        <InterruptDropdownHeader
          $open={open}
          onClick={() => setOpen((v) => !v)}
        >
          <InterruptMenuItemRow>
            {selected.icon}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InterruptMenuItemTitle>{selected.label}</InterruptMenuItemTitle>
                {selected.badge && <InterruptBadge>{selected.badge}</InterruptBadge>}
              </Box>
              <InterruptMenuItemDescription>
                {selected.desc}
              </InterruptMenuItemDescription>
            </Box>
          </InterruptMenuItemRow>
          <InterruptExpandIcon $open={open} />
        </InterruptDropdownHeader>

        {open &&
          options.map((o, i) => (
            <InterruptDropdownItem
              key={o.value}
              $isSelected={interruptMode === o.value}
              $isLast={i === options.length - 1}
              onClick={() => {
                onInterruptModeChange(o.value);
                setOpen(false);
              }}
            >
              <InterruptMenuItemRow>
                {o.icon}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InterruptMenuItemTitle>{o.label}</InterruptMenuItemTitle>
                    {o.badge && <InterruptBadge>{o.badge}</InterruptBadge>}
                  </Box>
                  <InterruptMenuItemDescription>
                    {o.desc}
                  </InterruptMenuItemDescription>
                </Box>
                <InterruptRadioOuter $isSelected={interruptMode === o.value}>
                  {interruptMode === o.value && <InterruptRadioInner />}
                </InterruptRadioOuter>
              </InterruptMenuItemRow>
            </InterruptDropdownItem>
          ))}
      </InterruptDropdown>

      {interruptMode === 'finish_step' && (
        <BridgeGapsBox>
          <Switch
            checked={bridgeRegionGaps}
            onChange={(e) => onBridgeRegionGapsChange(e.target.checked)}
          />
          <Box>
            <BridgeGapsTitle>
              {t('assistant.scriptDialog.bridgeRegionGaps')}
            </BridgeGapsTitle>
            <BridgeGapsDescription>
              {t('assistant.scriptDialog.bridgeRegionGapsDesc')}
            </BridgeGapsDescription>
          </Box>
        </BridgeGapsBox>
      )}
    </InterruptSectionContainer>
  );
};

export default InterruptSection;
